import "@/utils/dayjs"

import dayjs from "dayjs"
import { has, set } from "lodash-es"
import { action, makeAutoObservable, observable } from "mobx"

import APIApp from "@/api/app"
import { IBanner } from "@/api/app/types/IBanner"
import { IUserGuide, ShowBanner } from "@/api/app/types/user"
import APISubscribeGuide, { type GuideInfo } from "@/api/subscribeGuide"
import { getValidLanguage } from "@/locales/config"
import IUserInfoStore, { UserInfoDataType } from "@/models/IUserInfo"
// import { previewPopupLink } from "@/pages/Popups/PopupsUtils"
import { getShopifyAdminUrl } from "@/utils/shopify-admin-url"
import { KeyOf, ValueOf } from "@/utils/types-utils"
/**
 * 用户信息 Store
 */
export default class UserInfoStore implements IUserInfoStore {
  @observable dataLoadedTime: number = 0
  @observable verifyAppEmbedTime: number = 0
  @observable data: UserInfoDataType = {
    jump_url: "",
    is_close_integration: 1,
    banners: [],
    created_at: 0,
    currency: "-",
    currency_format: "-",
    email: "",
    iana_timezone: "Asia/Hong_Kong",
    id: 0,
    country: "",
    country_code: "",
    is_unlock_upload_logo: 0,
    is_remove_brand: 0,
    balance: 0,
    is_show_recharge_banner: false,
    // operate_log: {
    //   Banner_ReviewsGuide: {
    //     displayed: false,
    //     status: 0,
    //   }
    // },
    primary_domain: "",
    rating: 0,
    shop: "default-shop.myshopify.com",
    shop_id: 0,
    shop_owner: "",
    last_login_at: 0,
    return_page_accessed: false,
    return_setting_accessed: false,
    finish_task_count: 0,
    should_finish_task_count: 5,
    is_close_remove_branding: 0,
    is_close_email_banner: 0,
    is_remove_return_page_brand: 0,
    is_enter_test_store: 0,
    is_close_praise: 0,
    is_follow_product: 0,
    sender_email: "",
    sender_name: "",
    store_name: "",
    is_close_praise_guide: false,
    test_store_data: {},
    oauth: "",
    path: "parcelpanel-returns",
    prefix_path: "apps",
    timeline: [],
    tracking_conn: false,
    gift_amount: 0,
    shop_name: "",
    app_name: "",
    success_flow_run_id: 0,
    customer_info: {
      customer_email: "",
      customer_phone: "",
      customer_name: "",
    },
    is_auto_refund: 1, // 标签自动退款功能
    shopify_plan: {
      displayName: "",
      name: "",
      partnerDevelopment: false,
      shopifyPlus: false,
    },
    cart_insurance_enable: 2,
    checkout_insurance_enable: 2,
    display_name: "", // 店铺版本 判断是否为plus商家
    money_format: "", // 货币短格式
    is_forbidden_visit: false, // 禁用特定的商家、域名和开发店铺限制
    wise_show: 2,
    label_history_show: 1, // 标签历史账单统计记录是否展示 1 展示 0 不展示
  }
  @observable showBanner: ShowBanner = {
    cw_ai_chatbot: 2,
    pp_tracking_banner: 2,
    subscribe_banner: 2, // 自身App套餐推荐图是否展示
    recharge_banner_bill: 2,
    recharge_banner_logistics: 2,
    currency_change_notify: 2,
    cw_subscribe_banner: 2, // 公司官网套餐推荐图是否展示
    return_page_sem: 2, // return_page页营销推广banner 1-显示 2-不显示
    book_demo: 2,
    cross_sell: 2,
    index_book_demo: 2, // 首页book demo banner 1-显示 2-不显示
    return_window_banner: 2, // 退货窗口期pp推荐banner 1-显示 2-不显示
    show_guide: 1, // 首页引导
    brand_show_banner: 1, // 1显示 2隐藏 品牌切换banner显示
    is_old_rewards_open: 2, // 旧版本是否配置 return rewards banner
    is_old_return_fee_open: 2, // 旧版本是否配置 return fee banner
    is_old_shipping_fee_open: 2, // 旧版本是否配置 shipping fee banner
    is_show_other_app: 1, // 是否展示其他应用推荐
    promote_loyalty: 1,
    index_return_protection: 1,
    carrier_payment_moved_banner: 1,
    billing_upgrade: 2,
    show_recommend_for_you: 1, // 首页 Recommended for you 推荐卡 1-显示 2-不显示
  }
  @observable guideData: IUserGuide = {
    return_page_accessed: false,
    return_setting_accessed: false,
    enable_default_carrier: false,
    setup_free_return_service: false,
    finish_task_count: 0,
    should_finish_task_count: 4,
    notifications_accessed: false,
    is_need_check_menu: false,
  }
  @observable guideExpand = true
  @observable edgeTabSelected = 0
  @observable quickSetupInitialized = false

  @observable guideInfo: GuideInfo = {
    finish_step: 0,
  }

  @action.bound
  setGuideInfo(val: GuideInfo) {
    this.guideInfo = val
  }

  @action.bound
  async updateGuideInfo() {
    const res = await APISubscribeGuide.getGuideSetting()
    if (res.code === 1000) {
      const isFinished = res.data.finish_step >= 5
      // 兼容一下之前的,如果之前没完成，就设置为0
      if (!isFinished) {
        res.data.finish_step = 0
      }
      this.setGuideInfo(res.data)
    }
  }

  constructor() {
    makeAutoObservable(this)
  }

  @action.bound
  setData(data: UserInfoDataType): void {
    // dayjs.tz.setDefault("Pacific/Pago_Pago")
    dayjs.tz.setDefault(data.iana_timezone)
    console.log(data.iana_timezone)

    this.data = data
    this.showBanner = data?.show_banner
  }

  @action.bound
  async updateUserInfo() {
    const res = await APIApp.user()
    if (res.code === 1000) {
      // 如果后端没有返回language_admin，用系统自带的
      const local = getValidLanguage(
        res.data.language_admin || window.systemLanguage,
      )
      res.data.language_admin = local
      this.setData(res.data)
    }
  }

  @action.bound
  setDataByPath<T>(path: string, val: T) {
    set(this.data, path, val)
  }

  @action.bound
  setExpand() {
    this.guideExpand = !this.guideExpand
  }

  @action.bound
  setEdgeTabSelected(selected: number) {
    this.edgeTabSelected = selected
  }

  @action.bound
  setGuideField(key: keyof IUserGuide, val: boolean): void {
    this.guideData = { ...this.guideData, [key]: val } as IUserGuide
  }

  @action.bound
  setUserGuideData(data: IUserGuide): void {
    this.guideData = data
    function getIndex() {
      const arr = [
        data.return_page_accessed,
        // data.setup_free_return_service,
        data.return_setting_accessed,
        // data.enable_default_carrier,
        data.notifications_accessed,
        data.return_request_processed,
      ]
      const index = arr.findIndex((item) => !item)
      return index
    }
    const index = getIndex()
    this.guideExpand = index !== -1
    this.dataLoadedTime = 0
    if (index !== -1) {
      this.edgeTabSelected = index
    }
  }

  @action.bound
  setDataLoadedTime(time: number): void {
    this.dataLoadedTime = time
  }

  @action.bound
  setVerifyAppEmbedTime(time: number): void {
    this.verifyAppEmbedTime = time
  }

  @action.bound
  setSingleData = (
    key: KeyOf<UserInfoDataType>,
    value: ValueOf<UserInfoDataType>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    has(this.data, key) && (this.data[key] = value)
  }

  @action.bound
  setBannerStatus = (slug: IBanner["slug"], show: IBanner["show"]) => {
    let idx = -1

    this.data.banners.forEach((banner, index) => {
      if (slug === banner.slug) {
        idx = index
        return false
      }
    })

    if (idx > -1) {
      this.data.banners[idx].show = show
    }
  }

  @action.bound
  setBalance(value: number) {
    return (this.data.balance = value)
  }

  @action.bound
  getCustomerURL(customerID: number): string {
    return getShopifyAdminUrl(
      this.data.shop,
      `customers/${customerID === 0 ? "" : customerID}`,
    )
  }

  @action.bound
  getMyShopifyDomain(): string {
    return this.data.shop
  }

  @action.bound
  getShopID(): number {
    return this.data.shop_id
  }

  @action.bound
  setShowBanner(type: string, val: number | boolean) {
    this.showBanner[type] = val
  }

  @action.bound
  closeBanner = async (kind: number, type: string) => {
    this.setShowBanner(type, 2)
    const res = await APIApp.closeBanner(kind)
    const { code } = res
    if (code !== 1000) {
      this.setShowBanner(type, 1)
    }
  }

  // @action.bound
  // get popupPreviewUrl(): string {
  //   return previewPopupLink(this.data.shop, this.data.popup_id)
  // }
}
